import { Task, TaskType } from '@griply/types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BellNotifications16,
  Calendar,
  ChevronRight,
  Flag,
  PenEdit16,
  TagEmpty16,
  Target,
  Warning,
} from 'shared/assets/icons';
import { SubMenu } from 'shared/components/connected/task-card-multi-select-context-menu';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import {
  MetaViewOptions,
  useOpenTaskDetail,
} from 'shared/contexts/task-detail';
import { focusOnEndOfEditableContent } from 'shared/utils/focus-on-end-of-element';
import { getPlatformShortcut } from 'shared/utils/get-platform-shortcut';

export type EditListProps = {
  task: Task;
  onClose: () => void;
  openSubMenu: (subMenu: SubMenu) => void;
  nameRef?: React.RefObject<HTMLDivElement>;
  onAction?: () => void;
  hideEdit?: boolean;
};

export const EditList: React.FC<EditListProps> = ({
  task,
  onClose,
  openSubMenu,
  nameRef,
  onAction,
  hideEdit,
}) => {
  const { t } = useTranslation();
  const openTaskDetail = useOpenTaskDetail();

  const isHabit = [TaskType.Habit, TaskType.Repeating].includes(task.type);

  const onEdit = () => {
    openTaskDetail(task, MetaViewOptions.Settings);

    if (nameRef?.current) {
      focusOnEndOfEditableContent(nameRef.current);
    }

    onAction?.();
    onClose();
  };

  const updatePriority = () => openSubMenu(SubMenu.Priority);
  const updateLinkedItem = () => openSubMenu(SubMenu.LinkedItem);
  const updateDeadlineDeadline = () => openSubMenu(SubMenu.DeadlineDeadline);
  const updateReminderTime = () => openSubMenu(SubMenu.ReminderTime);
  const updateDate = () => openSubMenu(SubMenu.Date);
  const updateTags = () => openSubMenu(SubMenu.Tags);

  return (
    <>
      {!hideEdit && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuButton onClick={onEdit} start={<Icon icon={PenEdit16} />}>
              {t('context-menus.task.labels.edit')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}

      {!isHabit && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={updateLinkedItem}
              start={<Icon icon={Target} />}
              end={<Icon icon={ChevronRight} />}
              endColor={Colors.Grey600}
              shortcut={['G']}
            >
              {t('context-menus.task.multi-select.options.linked-item.label')}
            </PopupMenuButton>
          </PopupMenuListItem>

          <PopupMenuListItem>
            <PopupMenuButton
              onClick={updateDate}
              start={<Icon icon={Calendar} />}
              end={<Icon icon={ChevronRight} />}
              endColor={Colors.Grey600}
              shortcut={['D']}
            >
              {t('context-menus.task.multi-select.options.date.label')}
            </PopupMenuButton>
          </PopupMenuListItem>

          <PopupMenuListItem>
            <PopupMenuButton
              onClick={updatePriority}
              start={<Icon icon={Warning} />}
              end={<Icon icon={ChevronRight} />}
              endColor={Colors.Grey600}
              shortcut={['P']}
            >
              {t('context-menus.task.multi-select.options.priority.label')}
            </PopupMenuButton>
          </PopupMenuListItem>

          <PopupMenuListItem>
            <PopupMenuButton
              onClick={updateDeadlineDeadline}
              start={<Icon icon={Flag} />}
              end={<Icon icon={ChevronRight} />}
              endColor={Colors.Grey600}
              shortcut={getPlatformShortcut(['Shift', 'D'])}
            >
              {t(
                'context-menus.task.multi-select.options.deadline-deadline.label',
              )}
            </PopupMenuButton>
          </PopupMenuListItem>

          <PopupMenuListItem>
            <PopupMenuButton
              onClick={updateTags}
              start={<Icon icon={TagEmpty16} />}
              end={<Icon icon={ChevronRight} />}
              endColor={Colors.Grey600}
              shortcut={['T']}
            >
              {t('context-menus.task.multi-select.options.tags.label')}
            </PopupMenuButton>
          </PopupMenuListItem>

          <PopupMenuListItem>
            <PopupMenuButton
              onClick={updateReminderTime}
              start={<Icon icon={BellNotifications16} />}
              end={<Icon icon={ChevronRight} />}
              endColor={Colors.Grey600}
              shortcut={['R']}
            >
              {t('context-menus.task.multi-select.options.reminder-time.label')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}
    </>
  );
};
